import apiClient from './apiClient';

export type DefectStatusFilter =
  | 'all'
  | 'open'
  | 'assigned'
  | 'fixing'
  | 'recheck_pending'
  | 'closed_passed'
  | 'closed_disputed'
  | 'void';

export type DefectSeverityFilter = 'all' | 'low' | 'medium' | 'high' | 'critical';

export interface DefectBoardRow {
  id: string;
  title: string;
  description: string | null;
  status: string;
  severity: string;
  defectType: string | null;
  createdAt: string;
  closedAt: string | null;
  ageDays: number;
  resolutionNotes: string | null;

  originalTaskId: string;
  originalTaskCode: string;
  originalTaskName: string;
  reworkCount: number;
  remedialTaskId: string | null;
  remedialTaskCode: string | null;
  remedialTaskStatus: string | null;

  unitId: string | null;
  unitCode: string | null;
  unitName: string | null;

  assignedToTeamId: string | null;
  assignedToTeamName: string | null;

  raisedById: string | null;
  raisedByName: string | null;
}

export interface DefectAnalytics {
  byStatus: Record<string, number>;
  bySeverity: Record<string, number>;
  byTeam: Array<{ teamId: string | null; teamName: string; count: number }>;
  byTaskType: Array<{ taskType: string; count: number }>;
  trendByMonth: Array<{ month: string; raised: number; closed: number }>;
  meanTimeToClose: {
    overallDays: number | null;
    byTeam: Array<{ teamId: string | null; teamName: string; days: number }>;
  };
}

export interface DefectsBoard {
  defects: DefectBoardRow[];
  totals: {
    count: number;
    open: number;
    inProgress: number;
    awaitingRecheck: number;
    closedPassed: number;
    closedDisputed: number;
    voided: number;
    bySeverity: Record<'low' | 'medium' | 'high' | 'critical', number>;
  };
}

class DefectService {
  async getDefectsBoard(
    projectId: string,
    filters: {
      status?: DefectStatusFilter;
      severity?: DefectSeverityFilter;
      teamId?: string;
      activeOnly?: boolean;
    } = {}
  ): Promise<DefectsBoard> {
    const params: Record<string, string> = {};
    if (filters.status && filters.status !== 'all') params.status = filters.status;
    if (filters.severity && filters.severity !== 'all') params.severity = filters.severity;
    if (filters.teamId) params.teamId = filters.teamId;
    if (filters.activeOnly) params.activeOnly = 'true';
    const response: any = await apiClient.get(`/defects/project/${projectId}`, { params });
    return response.data;
  }

  async getAnalytics(params: { projectId?: string; from?: string; to?: string } = {}): Promise<DefectAnalytics> {
    const response: any = await apiClient.get('/defects/analytics', { params });
    return response.data;
  }
}

export default new DefectService();
